import type { UuidVersion } from '#layers/base/public';
import { DEFAULT_UUID_VERSION } from '#layers/base/public';

export interface ConnectorManagedDefaultValue {
	isAutoincrement: boolean | null;
}

export interface DefaultValueConnectorManaged extends ConnectorManagedDefaultValue {
	source: 'connectorManaged';
}

/**
 * What an engine-managed default resolves to at write time. Mirrors the Rust
 * `EngineDefaultSource` wire format.
 */
export type EngineDefaultSource =
	| { kind: 'static'; valueEncoded: unknown }
	| { kind: 'currentTimestamp' }
	| { kind: 'currentUser' }
	| { kind: 'randomUuid'; version: UuidVersion };

/**
 * Which write operations an engine-managed default fires on, and the source to
 * resolve for each. Mirrors the Rust `EngineManagedDefaultValue` wire format.
 * At least one of `onCreate`/`onUpdate` is set.
 */
export interface EngineManagedDefaultValue {
	onCreate?: EngineDefaultSource;
	onUpdate?: EngineDefaultSource;
}

export interface DefaultValueEngineManaged extends EngineManagedDefaultValue {
	source: 'engineManaged';
}

export type DefaultValue = DefaultValueConnectorManaged | DefaultValueEngineManaged;

export function isConnectorManaged(dv: DefaultValue): dv is DefaultValueConnectorManaged {
	return dv.source === 'connectorManaged';
}

export function isEngineManaged(dv: DefaultValue): dv is DefaultValueEngineManaged {
	return dv.source === 'engineManaged';
}

export function isAutoincrement(dv: DefaultValue | null | undefined): boolean {
	return dv != null && isConnectorManaged(dv) && dv.isAutoincrement === true;
}

export function createStaticDefault(value: unknown): DefaultValueEngineManaged {
	return {
		source: 'engineManaged',
		onCreate: { kind: 'static', valueEncoded: value },
	};
}

export function createAutoincrementDefault(): DefaultValueConnectorManaged {
	return {
		source: 'connectorManaged',
		isAutoincrement: true,
	};
}

export function createRandomUuidDefault(version: UuidVersion): DefaultValueEngineManaged {
	return {
		source: 'engineManaged',
		onCreate: { kind: 'randomUuid', version },
	};
}

export function getConfiguredUuidVersion(dv: DefaultValue | null | undefined): UuidVersion {
	if (dv?.source === 'engineManaged' && dv.onCreate?.kind === 'randomUuid' && dv.onUpdate === undefined) {
		return dv.onCreate.version;
	}

	return DEFAULT_UUID_VERSION;
}

export function createCurrentTimestampDefault({ onUpdate }: { onUpdate: boolean }): DefaultValueEngineManaged {
	return {
		source: 'engineManaged',
		onCreate: { kind: 'currentTimestamp' },
		...(onUpdate ? { onUpdate: { kind: 'currentTimestamp' } } : {}),
	};
}

export function createCurrentUserDefault({ onUpdate }: { onUpdate: boolean }): DefaultValueEngineManaged {
	return {
		source: 'engineManaged',
		onCreate: { kind: 'currentUser' },
		...(onUpdate ? { onUpdate: { kind: 'currentUser' } } : {}),
	};
}

export function getStaticDefaultValue(dv: DefaultValue | null | undefined): unknown {
	if (dv == null || !isEngineManaged(dv)) return undefined;

	// `onUpdate` defaults don't apply on create, so they contribute no form default.
	const createSource = dv.onCreate;

	return createSource?.kind === 'static' ? createSource.valueEncoded : undefined;
}
